import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BiPlus, BiTrash } from "react-icons/bi";

// Import your configured client (handles token, loader, and alerts automatically)
import axiosClient from "../utils/apiClient.js";

const TimeAndExpensesDashboard = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // 1. Get User Data
  const user = JSON.parse(localStorage.getItem("user"));
  
  // 2. Derive Permissions (Booleans are stable primitives)
  const isAdmin = user?.roleName === "admin";
  const isCaseManager = user?.roleName === "case_manager";
  const isFieldAgent = user?.roleName === "field_agent";

  const canViewAllEntries = isAdmin || isCaseManager;
  const canDelete = isAdmin || isCaseManager;

  useEffect(() => {
    const fetchTimeEntries = async () => {
      // If no user is found, stop immediately
      if (!user?.id) {
         setLoading(false);
         return;
      }

      try {
        let response;

        // 3. Logic to fetch data based on role
        if (canViewAllEntries) {
          // axiosClient handles the Base URL automatically
          response = await axiosClient.get(`/time-entries/all`);
        } else if (isFieldAgent) {
          response = await axiosClient.get(`/time-entries/user/${user.id}`);
        } else {
          setError("You don't have permission to view this page.");
          setLoading(false);
          return;
        }

        setTimeEntries(response.data);
      } catch (err) {
        console.error("Error fetching time entries:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load time entries. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTimeEntries();

  // 4. FIX: Use 'user?.id' instead of 'user' object to prevent infinite loop
  }, [user?.id, canViewAllEntries, isFieldAgent]); 

  // Filter time entries based on search query
  const filteredEntries = timeEntries.filter((entry) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const caseName = (entry.case_name || "").toLowerCase();
    const userName = `${entry.first_name || ""} ${entry.last_name || ""}`.toLowerCase();
    const desc = (entry.description || "").toLowerCase();
    return caseName.includes(q) || userName.includes(q) || desc.includes(q);
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredEntries.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected time entry/entries?`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      await axiosClient.post(`/time-entries/bulk-delete`, {
        ids: selectedIds,
      });
      setTimeEntries((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete error, attempting fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) => axiosClient.delete(`/time-entries/${id}`))
        );
        setTimeEntries((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
        setSelectedIds([]);
      } catch (fallbackErr) {
        setError(
          err.response?.data?.message || "Failed to delete selected time entries."
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (window.confirm("Are you sure you want to delete this time entry?")) {
      try {
        // 5. Use axiosClient for delete (Auto-loader & Auto-toast)
        await axiosClient.delete(`/time-entries/${entryId}`);

        // Update UI state only after success
        setTimeEntries((prev) => prev.filter((entry) => entry.id !== entryId));
        setSelectedIds((prev) => prev.filter((id) => id !== entryId));
      } catch (err) {
        console.error("Delete failed", err);
        // Error is handled globally by axiosClient, but we log it here.
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Loading time entries...
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  // Calculate totals based on filtered
  const totalHours = filteredEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.hours) || 0),
    0
  );

  const totalExpenses = filteredEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.expense_amount) || 0),
    0
  );

  const isAllSelected =
    filteredEntries.length > 0 && selectedIds.length === filteredEntries.length;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Time & Expenses</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 border rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
          />
          {(isAdmin || isCaseManager || isFieldAgent) && (
            <Link
              to="/time-entries/new"
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
            >
              <BiPlus className="mr-2" /> Log New Entry
            </Link>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <p className="text-gray-500 text-sm">Total Hours</p>
          <p className="text-2xl font-semibold text-gray-900">
            {totalHours.toFixed(2)}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <p className="text-gray-500 text-sm">Total Expenses</p>
          <p className="text-2xl font-semibold text-gray-900">
            ${totalExpenses.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Bulk actions banner */}
      {canDelete && selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>
              {selectedIds.length} of {filteredEntries.length} selected
            </span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md shadow-sm transition-colors"
          >
            <BiTrash className="w-4 h-4" />
            <span>{isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}</span>
          </button>
        </div>
      )}

      {/* Desktop / Responsive Table View */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {canDelete && (
                <th scope="col" className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Case
              </th>
              {canViewAllEntries && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field Agent
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Expenses
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              {canDelete && (
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((entry) => {
                const isSelected = selectedIds.includes(entry.id);
                return (
                  <tr
                    key={entry.id}
                    className={`hover:bg-gray-50 transition-colors duration-200 ${
                      isSelected ? "bg-blue-50/50" : ""
                    }`}
                  >
                    {canDelete && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(entry.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(entry.entry_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.case_name}
                    </td>
                    {canViewAllEntries && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.first_name} {entry.last_name}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.hours}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${parseFloat(entry.expense_amount).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      {entry.description}
                    </td>
                    {canDelete && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <BiTrash className="inline-block h-5 w-5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={canDelete ? (canViewAllEntries ? "8" : "7") : canViewAllEntries ? "7" : "6"}
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No time entries found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimeAndExpensesDashboard;